import { CaretLeft, CaretRight } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * TablePagination — the one pagination bar.
 *
 * "Rows per page" + the total on the left, Previous / Page X of Y / Next on the right. Every
 * list that can grow without bound should sit on top of this instead of growing its own
 * footer: the app had four different hand-rolled pagination footers, each with its own page
 * size, its own wording and its own idea of what to show when there is a single page.
 *
 * The page number is owned by the caller, so the list can reset it when a filter changes.
 */

/** Page sizes offered everywhere. */
export const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

interface Props {
  /** 1-based. Clamped for display, so a stale page never renders "Page 7 of 3". */
  page: number;
  pageSize: number;
  /** Rows BEFORE paging — i.e. everything the current filter matched. */
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  options?: number[];
  className?: string;
}

/** Rows for the current page. Clamps the page, so an out-of-range page shows the last one. */
export function pageSlice<T>(rows: T[], page: number, pageSize: number): T[] {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  return rows.slice((safePage - 1) * pageSize, safePage * pageSize);
}

export function TablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  options = PAGE_SIZE_OPTIONS,
  className,
}: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 border-t border-border px-3 py-3 sm:gap-3 sm:px-4',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground sm:gap-2">
        <label htmlFor="rows-per-page" className="shrink-0">
          <span className="sm:hidden">Rows:</span>
          <span className="hidden sm:inline">Rows per page:</span>
        </label>
        <select
          id="rows-per-page"
          value={pageSize}
          // A new page size invalidates the current page — 50 rows a page has no page 7.
          onChange={(e) => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}
          className="h-8 shrink-0 rounded-md border border-border bg-card px-1 text-xs text-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100/50 sm:px-2"
        >
          {options.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span className="truncate">{total.toLocaleString()} total</span>
      </div>

      <div className="flex shrink-0 items-center gap-1 text-xs sm:gap-2">
        <Button
          variant="outline"
          size="xs"
          className="px-1.5 sm:px-2.5"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          aria-label="Previous page"
        >
          <CaretLeft className="h-3.5 w-3.5 sm:hidden" />
          <span className="hidden sm:inline">Previous</span>
        </Button>
        <span className="whitespace-nowrap px-0.5 text-foreground sm:px-1">
          <span className="sm:hidden">{safePage} / {totalPages}</span>
          <span className="hidden sm:inline">Page {safePage} of {totalPages}</span>
        </span>
        <Button
          variant="outline"
          size="xs"
          className="px-1.5 sm:px-2.5"
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
          aria-label="Next page"
        >
          <CaretRight className="h-3.5 w-3.5 sm:hidden" />
          <span className="hidden sm:inline">Next</span>
        </Button>
      </div>
    </div>
  );
}
